/**
 * @fileoverview Анализ сцены и автоподбор параметров пост-обработки
 * @module MikhmelViewer/Cinematic10/AIPresets
 *
 * SceneAnalyzer — снимает признаки кадра на GPU (редукция в compute-шейдере)
 * и отдаёт их вектором. Чтение с GPU асинхронное и стоит дорого, поэтому
 * анализ идёт с интервалом, а не каждый кадр.
 *
 * AIPresetOptimizer — превращает вектор признаков в набор параметров.
 * Работает в двух режимах:
 *   • ONNX-модель, если она обучена и загружена
 *   • эвристики, если модели нет
 *
 * Эвристический режим — не заглушка, а рабочий путь по умолчанию. Модели
 * ещё не существует: чтобы её обучить, нужен размеченный датасет пар
 * «сцена → параметры, которые выбрал рентгенолог», и собрать его можно
 * только из телеметрии реальных пользователей. Метод collectTrainingSample()
 * ниже как раз пишет такие пары — с них и начинается датасет.
 */

import { parseSceneStats, COMPUTE_PASSES } from './wgslCompute.js';
import { ensureOrt, getExecutionProviders } from './ortLoader.js';

const Ok = (value) => ({ success: true, value });
const Err = (error) => ({ success: false, error });

let _log = { debug: () => {}, info: () => {}, warn: () => {}, error: () => {} };
export function setPresetLogger(logger) { if (logger) _log = logger; }

const STATS_BUFFER_SIZE = COMPUTE_PASSES.sceneStats.statsBufferSize;
const STATS_BUFFER_NAME = 'sceneStats';

// ═══════════════════════════════════════════════════════════════════
// SCENE ANALYZER
// ═══════════════════════════════════════════════════════════════════

export class SceneAnalyzer {
  constructor(backend, options = {}) {
    this._backend = backend;
    this._intervalMs = options.intervalMs ?? 500;
    this._sampleStride = options.sampleStride ?? 4;
    this._lastAnalysis = 0;
    this._pending = false;
    this._features = null;
    this._history = [];
    this._historySize = options.historySize ?? 8;
    this._disposed = false;
  }

  /** Достаточно ли времени прошло с прошлого замера. */
  shouldAnalyze(now = performance.now()) {
    if (this._disposed || this._pending) return false;
    return now - this._lastAnalysis >= this._intervalMs;
  }

  /**
   * Записать сбор статистики в текущий кадр. Вызывается внутри
   * beginFrame/endFrame, само чтение произойдёт позже.
   */
  recordPass({ srcView, depthView, width, height, hasDepth }) {
    if (this._disposed || !this._backend?.isReady()) return false;

    this._backend.ensureStorageBuffer(STATS_BUFFER_NAME, STATS_BUFFER_SIZE);
    this._backend.clearStorageBuffer(STATS_BUFFER_NAME, STATS_BUFFER_SIZE);

    // minLuma накапливается через atomicMin — стартовое значение должно быть
    // максимальным, иначе минимум навсегда останется нулём после clearBuffer
    this._backend.writeStorageBuffer(
      STATS_BUFFER_NAME, new Uint32Array([0xFFFFFFFF]), 3 * 4
    );

    const stride = Math.max(1, this._sampleStride);
    this._backend.runComputePass('sceneStats', {
      resources: {
        texSrc: srcView,
        texDepth: depthView || srcView,
        stats: this._backend.getStorageBuffer(STATS_BUFFER_NAME),
      },
      uniforms: {
        uResolution: [width, height],
        uSampleStride: stride,
        uHasDepth: hasDepth ? 1 : 0,
      },
      dispatch: [Math.ceil(width / stride), Math.ceil(height / stride), 1],
    });

    this._pending = true;
    return true;
  }

  /** Асинхронно забрать результат. Вызывать после endFrame(). */
  async collect() {
    if (!this._pending || this._disposed) return null;

    try {
      const raw = await this._backend.readStorageBuffer(STATS_BUFFER_NAME, STATS_BUFFER_SIZE);
      this._pending = false;
      this._lastAnalysis = performance.now();

      if (!raw) return null;

      const stats = parseSceneStats(new Uint32Array(raw));
      const features = this._buildFeatures(stats);

      this._features = features;
      this._history.push(features);
      if (this._history.length > this._historySize) this._history.shift();

      return features;
    } catch (e) {
      this._pending = false;
      _log.warn('[SceneAnalyzer] Чтение статистики не удалось:', e.message);
      return null;
    }
  }

  _buildFeatures(stats) {
    const hist = stats.histogram;

    // Энтропия гистограммы: насколько равномерно распределены яркости.
    // Низкая — кадр «плоский», почти весь в одном тоне.
    let entropy = 0;
    for (const p of hist) {
      if (p > 1e-6) entropy -= p * Math.log2(p);
    }
    entropy /= 4; // нормировка на log2(16)

    // Динамический диапазон по перцентилям, а не по min/max — единичные
    // выбросы не должны определять экспозицию всей сцены
    const p05 = this._percentile(hist, 0.05);
    const p95 = this._percentile(hist, 0.95);

    const prev = this._history.length ? this._history[this._history.length - 1] : null;
    const motionIntensity = prev
      ? Math.min(1, Math.abs(stats.brightness - prev.brightness) * 8 +
                    Math.abs(stats.complexity - prev.complexity) * 4)
      : 0;

    return {
      brightness: stats.brightness,
      contrast: Math.min(1, stats.contrast),
      complexity: Math.min(1, stats.complexity * 6),
      edgeDensity: stats.edgeRatio,
      darkRatio: stats.darkRatio,
      brightRatio: stats.brightRatio,
      dynamicRange: Math.max(0, p95 - p05),
      entropy: Math.min(1, entropy),
      meanDepth: stats.meanDepth,
      motionIntensity,
      histogram: hist,
      raw: stats,
      timestamp: performance.now(),
    };
  }

  _percentile(histogram, q) {
    let acc = 0;
    for (let i = 0; i < histogram.length; i++) {
      acc += histogram[i];
      if (acc >= q) return (i + 0.5) / histogram.length;
    }
    return 1;
  }

  getFeatures() { return this._features; }
  getHistory() { return [...this._history]; }

  /** Сглаженные признаки — гасят дрожание автоподбора между кадрами. */
  getSmoothedFeatures() {
    if (this._history.length === 0) return null;
    const keys = ['brightness', 'contrast', 'complexity', 'edgeDensity',
                  'darkRatio', 'brightRatio', 'dynamicRange', 'entropy',
                  'meanDepth', 'motionIntensity'];
    const out = {};
    for (const k of keys) {
      out[k] = this._history.reduce((a, f) => a + (f[k] || 0), 0) / this._history.length;
    }
    return out;
  }

  reset() { this._history = []; this._features = null; this._pending = false; }
  dispose() { this._disposed = true; this.reset(); }
}

// ═══════════════════════════════════════════════════════════════════
// AI PRESET OPTIMIZER
// ═══════════════════════════════════════════════════════════════════

/** Порядок признаков во входном тензоре модели. Менять только вместе с моделью. */
export const FEATURE_ORDER = Object.freeze([
  'brightness', 'contrast', 'complexity', 'edgeDensity',
  'darkRatio', 'brightRatio', 'dynamicRange', 'entropy',
  'meanDepth', 'motionIntensity',
]);

/** Порядок выходов модели. */
export const OUTPUT_ORDER = Object.freeze([
  'ssaoIntensity', 'ssaoRadius',
  'bloomIntensity', 'bloomThreshold',
  'exposure', 'contrast', 'saturation',
  'sharpen', 'vignetteIntensity',
  'volumetricIntensity', 'ssgiIntensity', 'denoiseStrength',
]);

export class AIPresetOptimizer {
  constructor(options = {}) {
    this._model = null;
    this._modelPath = options.modelPath || '/models/preset_optimizer.onnx';
    this._enabled = options.enabled !== false;
    this._mode = 'heuristic';
    this._blendFactor = options.blendFactor ?? 0.15;
    this._lastResult = null;
    this._trainingSamples = [];
    this._maxSamples = options.maxSamples ?? 500;
    this._disposed = false;
  }

  /**
   * Загрузка ONNX-модели. Отсутствие модели — не ошибка: оптимизатор
   * останется в эвристическом режиме и продолжит работать.
   */
  async loadModel(path = null) {
    const modelPath = path || this._modelPath;
    try {
      const ort = await ensureOrt();
      if (!ort) return Err('ONNX Runtime Web недоступен');

      this._model = await ort.InferenceSession.create(modelPath, {
        executionProviders: getExecutionProviders(),
        graphOptimizationLevel: 'all',
      });

      this._mode = 'neural';
      _log.info(`[AIPresetOptimizer] Модель загружена: ${modelPath}`);
      return Ok(true);
    } catch (e) {
      this._model = null;
      this._mode = 'heuristic';
      _log.warn(`[AIPresetOptimizer] Модель недоступна (${e.message}) — работаем на эвристиках`);
      return Err(e.message);
    }
  }

  getMode() { return this._mode; }
  hasModel() { return !!this._model; }
  setEnabled(v) { this._enabled = !!v; }
  isEnabled() { return this._enabled; }

  /**
   * Подобрать параметры под признаки сцены.
   * @param {object} features выход SceneAnalyzer
   * @returns {Promise<object>} частичный объект параметров для setParams()
   */
  async optimize(features) {
    if (!this._enabled || !features || this._disposed) return null;

    let raw;
    if (this._model) {
      raw = await this._runModel(features);
      if (!raw) raw = this._runHeuristics(features);
    } else {
      raw = this._runHeuristics(features);
    }

    const params = this._toParams(raw);
    this._lastResult = { raw, params, features, timestamp: performance.now() };
    return params;
  }

  async _runModel(features) {
    try {
      const ort = await ensureOrt();
      if (!ort) return null;

      const input = new Float32Array(FEATURE_ORDER.length);
      FEATURE_ORDER.forEach((key, i) => { input[i] = Number(features[key]) || 0; });

      const tensor = new ort.Tensor('float32', input, [1, FEATURE_ORDER.length]);
      const results = await this._model.run({ input: tensor });
      const outKey = Object.keys(results)[0];
      const data = results[outKey].data;

      const out = {};
      OUTPUT_ORDER.forEach((key, i) => { out[key] = data[i]; });
      return out;
    } catch (e) {
      _log.warn('[AIPresetOptimizer] Инференс не удался, откат на эвристики:', e.message);
      return null;
    }
  }

  /**
   * Эвристики. Правила выведены из того, как обычно настраивают
   * визуализацию медицинских объёмов вручную.
   */
  _runHeuristics(f) {
    const brightness = f.brightness ?? 0.5;
    const contrast = f.contrast ?? 0.5;
    const complexity = f.complexity ?? 0.5;
    const edges = f.edgeDensity ?? 0.2;
    const darkRatio = f.darkRatio ?? 0.3;
    const brightRatio = f.brightRatio ?? 0.05;
    const dynamicRange = f.dynamicRange ?? 0.6;
    const motion = f.motionIntensity ?? 0;

    // Экспозиция тянет среднюю яркость к 0.45 — комфортному уровню
    // для просмотра на медицинском мониторе в затемнённой комнате
    const targetBrightness = 0.45;
    let exposure = brightness > 0.02
      ? Math.max(0.6, Math.min(2.2, targetBrightness / brightness))
      : 1.4;

    // Пересвет опаснее недосвета: выбитые светы означают потерю данных
    if (brightRatio > 0.12) exposure *= 0.82;
    if (darkRatio > 0.55) exposure *= 1.12;

    // Контраст добираем только там, где его не хватает
    const contrastBoost = dynamicRange < 0.45
      ? 1.0 + (0.45 - dynamicRange) * 0.9
      : 1.0 + Math.max(0, 0.25 - contrast) * 0.4;

    // SSAO делает объём читаемым, но на сложной геометрии превращается
    // в грязь — поэтому интенсивность падает с ростом плотности деталей
    const ssaoIntensity = Math.max(0.2, Math.min(0.85, 0.75 - complexity * 0.35));
    const ssaoRadius = Math.max(4, Math.min(14, 12 - complexity * 7));

    // Bloom оправдан только при наличии ярких областей
    const bloomIntensity = brightRatio > 0.02
      ? Math.min(0.45, 0.12 + brightRatio * 1.6)
      : 0.08;
    const bloomThreshold = Math.max(0.35, Math.min(0.85, 0.55 + brightness * 0.45));

    // Резкость обратна плотности границ: где их и так много, подъём
    // резкости только усиливает шум
    const sharpen = Math.max(0.1, Math.min(0.9, 0.7 - edges * 1.8));

    // Насыщенность: тусклые сцены слегка оживляем, яркие не трогаем
    const saturation = 1.0 + Math.max(0, 0.35 - contrast) * 0.5;

    // Виньетка удерживает взгляд в центре, но на светлых кадрах заметна
    const vignetteIntensity = Math.max(0.1, Math.min(0.5, 0.35 - brightness * 0.25));

    // Объёмный свет читается на тёмном фоне и теряется на светлом
    const volumetricIntensity = darkRatio > 0.35
      ? Math.min(0.7, 0.25 + darkRatio * 0.6)
      : 0.15;

    // SSGI тем полезнее, чем больше в кадре затенённых областей
    const ssgiIntensity = Math.max(0.1, Math.min(0.6, 0.2 + darkRatio * 0.5));

    // При движении шум маскируется, в статике — виден: денойзим сильнее в покое
    const denoiseStrength = motion > 0.25
      ? Math.max(0.3, 0.7 - motion * 0.5)
      : Math.min(1.0, 0.7 + complexity * 0.3);

    return {
      ssaoIntensity, ssaoRadius,
      bloomIntensity, bloomThreshold,
      exposure, contrast: contrastBoost, saturation,
      sharpen, vignetteIntensity,
      volumetricIntensity, ssgiIntensity, denoiseStrength,
    };
  }

  /** Преобразование плоского вектора в структуру параметров рендерера. */
  _toParams(raw) {
    const clamp = (v, lo, hi, fallback) => {
      const n = Number(v);
      return Number.isFinite(n) ? Math.max(lo, Math.min(hi, n)) : fallback;
    };

    return {
      ssao: {
        intensity: clamp(raw.ssaoIntensity, 0, 1, 0.6),
        radius: clamp(raw.ssaoRadius, 1, 24, 8),
      },
      bloom: {
        intensity: clamp(raw.bloomIntensity, 0, 1, 0.25),
        threshold: clamp(raw.bloomThreshold, 0.1, 1.5, 0.65),
      },
      toneMap: {
        exposure: clamp(raw.exposure, 0.3, 3, 1.3),
      },
      colorGrade: {
        contrast: clamp(raw.contrast, 0.5, 2, 1.05),
        saturation: clamp(raw.saturation, 0, 2, 1.1),
      },
      sharpen: clamp(raw.sharpen, 0, 2, 0.4),
      vignette: {
        intensity: clamp(raw.vignetteIntensity, 0, 1, 0.35),
      },
      volumetric: {
        intensity: clamp(raw.volumetricIntensity, 0, 2, 0.5),
      },
      ssgi: {
        intensity: clamp(raw.ssgiIntensity, 0, 2, 0.4),
      },
      denoise: {
        strength: clamp(raw.denoiseStrength, 0, 1, 0.7),
      },
    };
  }

  /**
   * Плавное смешивание с текущими параметрами. Резкий скачок настроек
   * посреди просмотра выглядит как сбой, поэтому переход растянут во времени.
   */
  blend(currentParams, targetParams, factor = null) {
    const t = factor ?? this._blendFactor;
    const out = {};

    const walk = (cur, tgt, dst) => {
      for (const key of Object.keys(tgt)) {
        const tv = tgt[key];
        const cv = cur?.[key];
        if (tv && typeof tv === 'object' && !Array.isArray(tv)) {
          dst[key] = {};
          walk(cv || {}, tv, dst[key]);
        } else if (typeof tv === 'number' && typeof cv === 'number') {
          dst[key] = cv + (tv - cv) * t;
        } else {
          dst[key] = tv;
        }
      }
    };

    walk(currentParams, targetParams, out);
    return out;
  }

  /**
   * Сохранить пару «признаки сцены → параметры, выбранные человеком».
   * Из накопленных пар и обучается модель, заменяющая эвристики.
   */
  collectTrainingSample(features, chosenParams, meta = {}) {
    if (!features || !chosenParams) return Err('Both features and params are required');

    const input = FEATURE_ORDER.map(k => Number(features[k]) || 0);
    const output = [
      chosenParams.ssao?.intensity ?? 0,
      chosenParams.ssao?.radius ?? 0,
      chosenParams.bloom?.intensity ?? 0,
      chosenParams.bloom?.threshold ?? 0,
      chosenParams.toneMap?.exposure ?? 1,
      chosenParams.colorGrade?.contrast ?? 1,
      chosenParams.colorGrade?.saturation ?? 1,
      chosenParams.sharpen ?? 0,
      chosenParams.vignette?.intensity ?? 0,
      chosenParams.volumetric?.intensity ?? 0,
      chosenParams.ssgi?.intensity ?? 0,
      chosenParams.denoise?.strength ?? 0,
    ];

    this._trainingSamples.push({ input, output, meta, timestamp: Date.now() });
    if (this._trainingSamples.length > this._maxSamples) this._trainingSamples.shift();

    return Ok(this._trainingSamples.length);
  }

  /** Выгрузка датасета в JSON — вход для оффлайн-обучения. */
  exportTrainingData() {
    return {
      version: 1,
      featureOrder: [...FEATURE_ORDER],
      outputOrder: [...OUTPUT_ORDER],
      samples: this._trainingSamples.map(s => ({ ...s })),
      count: this._trainingSamples.length,
      exportedAt: new Date().toISOString(),
    };
  }

  clearTrainingData() { this._trainingSamples = []; }
  getTrainingSampleCount() { return this._trainingSamples.length; }
  getLastResult() { return this._lastResult; }

  dispose() {
    this._disposed = true;
    try { this._model?.release?.(); } catch { /* нечего освобождать */ }
    this._model = null;
    this._trainingSamples = [];
  }
}
